import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { FulfillmentDetails } from '@/hooks/bookings/types';
import { useFileUpload } from '@/hooks/useFileUpload';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { format } from 'date-fns';
import { CalendarIcon, Upload, X, Plus, Save, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

interface FulfillmentFormProps {
  fulfillmentDetails: FulfillmentDetails | null;
  bookingId: string;
  onSave: () => Promise<void>;
  onCancel: () => void;
}

const FulfillmentForm: React.FC<FulfillmentFormProps> = ({
  fulfillmentDetails,
  bookingId,
  onSave,
  onCancel
}) => {
  const { toast } = useToast();
  const { uploadFile, uploading } = useFileUpload();
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState<FulfillmentDetails>({
    waybill_number: fulfillmentDetails?.waybill_number || '',
    tracking_url: fulfillmentDetails?.tracking_url || '',
    courier_name: fulfillmentDetails?.courier_name || '',
    courier_logo_url: fulfillmentDetails?.courier_logo_url || '',
    estimated_delivery_date: fulfillmentDetails?.estimated_delivery_date || null,
    signature_url: fulfillmentDetails?.signature_url || '',
    attachments: fulfillmentDetails?.attachments || [],
    updates: fulfillmentDetails?.updates || []
  });

  const [newUpdate, setNewUpdate] = useState({ status: '', notes: '' });

  const handleInputChange = (field: keyof FulfillmentDetails, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleFileUpload = async (file: File, field: 'signature_url' | 'courier_logo_url') => {
    try {
      const fileUrl = await uploadFile(file, 'fulfillment-files');
      if (fileUrl) {
        handleInputChange(field, fileUrl);
        toast({
          title: "File uploaded successfully",
          description: "The file has been uploaded and saved.",
        });
      }
    } catch (error) {
      toast({
        title: "Upload failed",
        description: "Failed to upload file. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleAttachmentUpload = async (file: File) => {
    try {
      const fileUrl = await uploadFile(file, 'fulfillment-attachments');
      if (fileUrl) {
        setFormData(prev => ({
          ...prev,
          attachments: [...prev.attachments, fileUrl]
        }));
        toast({
          title: "Attachment uploaded",
          description: "The attachment has been added successfully.",
        });
      }
    } catch (error) {
      toast({
        title: "Upload failed",
        description: "Failed to upload attachment. Please try again.",
        variant: "destructive",
      });
    }
  };

  const removeAttachment = (index: number) => {
    setFormData(prev => ({
      ...prev,
      attachments: prev.attachments.filter((_, i) => i !== index)
    }));
  };

  const addUpdate = () => {
    if (!newUpdate.status.trim()) return;

    const update = {
      timestamp: new Date().toISOString(),
      status: newUpdate.status.trim(),
      notes: newUpdate.notes.trim()
    };

    setFormData(prev => ({
      ...prev,
      updates: [...prev.updates, update]
    }));

    setNewUpdate({ status: '', notes: '' });
  };

  const removeUpdate = (index: number) => {
    setFormData(prev => ({
      ...prev,
      updates: prev.updates.filter((_, i) => i !== index)
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Step 1: Save fulfillment details to booking
      const { error: updateError } = await supabase
        .from('bookings')
        .update({ fulfillment_details: formData as any })
        .eq('id', bookingId);

      if (updateError) throw updateError;

      // Step 2: Update booking status to trigger fulfillment events
      // Determine appropriate status based on tracking URL presence
      const newStatus = formData.tracking_url ? 'in_transit' : 'awaiting_fulfillment';
      
      const { error: statusError } = await supabase.rpc('update_booking_status', {
        p_booking_id: bookingId,
        p_new_status: newStatus,
        p_updated_by: null,
        p_notes: `Fulfillment details updated - Waybill: ${formData.waybill_number || 'N/A'}`
      });

      if (statusError) {
        console.error('Status update failed:', statusError);
        toast({
          title: "Details saved, but status not updated",
          description: "Fulfillment details saved but automatic status change failed.",
          variant: "default",
        });
      } else {
        toast({
          title: "Fulfillment details updated",
          description: "Details saved and customer will be notified automatically.",
        });
      }

      await onSave();
    } catch (error) {
      console.error('Error updating fulfillment details:', error);
      toast({
        title: "Failed to save",
        description: "Could not save fulfillment details. Please try again.",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="bg-card border">
      <CardHeader>
        <CardTitle className="text-xl font-semibold">Edit Fulfillment Details</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Basic Information */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="waybill">Waybill Number</Label>
            <Input
              id="waybill"
              value={formData.waybill_number}
              onChange={(e) => handleInputChange('waybill_number', e.target.value)}
              placeholder="Enter waybill number"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="courier">Courier Name</Label>
            <Input
              id="courier"
              value={formData.courier_name}
              onChange={(e) => handleInputChange('courier_name', e.target.value)}
              placeholder="Enter courier name"
            />
          </div>
        </div>

        {/* Tracking URL */}
        <div className="space-y-2">
          <Label htmlFor="tracking">Tracking URL</Label>
          <Input
            id="tracking"
            type="url"
            value={formData.tracking_url}
            onChange={(e) => handleInputChange('tracking_url', e.target.value)}
            placeholder="https://tracking.courier.com/track?number=..."
          />
        </div>

        {/* Estimated Delivery Date */}
        <div className="space-y-2">
          <Label>Estimated Delivery Date</Label>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className={cn(
                  "w-full justify-start text-left font-normal",
                  !formData.estimated_delivery_date && "text-muted-foreground"
                )}
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {formData.estimated_delivery_date ? 
                  format(new Date(formData.estimated_delivery_date), 'PPP') : 
                  'Pick a date'
                }
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0">
              <Calendar
                mode="single"
                selected={formData.estimated_delivery_date ? new Date(formData.estimated_delivery_date) : undefined}
                onSelect={(date) => handleInputChange('estimated_delivery_date', date?.toISOString() || null)}
                initialFocus
              />
            </PopoverContent>
          </Popover>
        </div>

        {/* File Uploads */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Courier Logo</Label>
            <div className="flex items-center gap-2">
              <Input
                type="file"
                accept="image/*"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) handleFileUpload(file, 'courier_logo_url');
                }}
                disabled={uploading}
              />
              {formData.courier_logo_url && (
                <img src={formData.courier_logo_url} alt="Courier logo" className="h-8 w-8 object-contain" />
              )}
            </div>
          </div>

          <div className="space-y-2">
            <Label>Signature</Label>
            <Input
              type="file"
              accept="image/*,.pdf"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) handleFileUpload(file, 'signature_url');
              }}
              disabled={uploading}
            />
          </div>
        </div>

        {/* Attachments */}
        <div className="space-y-2">
          <Label>Additional Attachments</Label>
          <div className="flex flex-wrap gap-2 mb-2">
            {formData.attachments.map((attachment, index) => (
              <div key={index} className="flex items-center gap-1 bg-muted px-2 py-1 rounded text-sm">
                <span>Attachment {index + 1}</span>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => removeAttachment(index)}
                  className="h-4 w-4 p-0"
                >
                  <X className="h-3 w-3" />
                </Button>
              </div>
            ))}
          </div>
          <Input
            type="file"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) handleAttachmentUpload(file);
            }}
            disabled={uploading}
          />
        </div>

        {/* Status Updates */}
        <div className="space-y-4">
          <Label>Status Updates</Label>
          
          {/* Existing Updates */}
          <div className="space-y-2">
            {formData.updates.map((update, index) => (
              <div key={index} className="flex items-center gap-2 p-2 bg-muted rounded">
                <div className="flex-1">
                  <div className="font-medium text-sm">{update.status}</div>
                  <div className="text-xs text-muted-foreground">
                    {format(new Date(update.timestamp), 'PPp')}
                  </div>
                  {update.notes && <div className="text-sm">{update.notes}</div>}
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => removeUpdate(index)}
                  className="h-8 w-8 p-0"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>

          {/* Add New Update */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            <Input
              placeholder="Status"
              value={newUpdate.status}
              onChange={(e) => setNewUpdate(prev => ({ ...prev, status: e.target.value }))}
            />
            <Input
              placeholder="Notes (optional)"
              value={newUpdate.notes}
              onChange={(e) => setNewUpdate(prev => ({ ...prev, notes: e.target.value }))}
            />
            <Button
              type="button"
              variant="outline"
              onClick={addUpdate}
              disabled={!newUpdate.status.trim()}
              className="w-full"
            >
              <Plus className="h-4 w-4 mr-1" />
              Add Update
            </Button>
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-2 justify-end pt-4 border-t">
          <Button variant="outline" onClick={onCancel} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Save Details
              </>
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default FulfillmentForm;
